<script setup>
// Auto-extracted chrome for SolarCheck26.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "SolarCheck 26" },
    brandTagline: { type: String, default: "EEG 2026 - jetzt prüfen" },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "SolarCheck 26");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "EEG 2026 - jetzt prüfen");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');


const fontsHref = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&display=swap";
</script>

<template>
    <div class="sc26-root" :style="`--sc26-accent:${brandAccent};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="sc26-topbar">
                    <div class="sc26-topbar-inner">
                        <div class="sc26-brand">
                            <span class="sc26-brand-mark">SC</span>
                            <div class="sc26-brand-text">
                                <span class="sc26-brand-name">{{ brandName }}</span>
                                <span class="sc26-brand-tag">{{ brandTagline }}</span>
                            </div>
                        </div>
                        <span class="sc26-byline">Pruefstand für die EEG-Novelle 2026 - unabhaengig, kostenlos, in 60 Sekunden</span>
                    </div>
                </header>
        <slot />
        <nav class="sc26-legal" aria-label="Rechtliche Hinweise">
                    <a href="/impressum">Impressum</a>
                    <span aria-hidden="true">·</span>
                    <a href="/datenschutz">Datenschutz</a>
                    <span aria-hidden="true">·</span>
                    <a href="/agb">AGB</a>
                    <span aria-hidden="true">·</span>
                    <a href="/widerruf">Widerruf</a>
                    <a href="/ratgeber">Ratgeber</a>
                </nav>
        <footer class="sc26-footer">
                        <span>{{ brandName }} - {{ DESIGN_VERSION }}</span>
                        <span>Redaktion: Fachredaktion Energierecht - Quellen: BNetzA, EEG 2023 i. d. F. 2026, Solarspitzengesetz</span>
                    </footer>
    </div>
</template>

<style scoped>

.sc26-root {
    --sc26-accent: v-bind(brandAccent);
    --sc26-ink: #15101f;
    --sc26-ink-soft: #4a4055;
    --sc26-paper: #fdfaff;
    --sc26-line: #ece2f4;
    --sc26-hot: #b91c1c;
    --sc26-warn: #b45309;
    --sc26-cool: #047857;
    font-family: 'Inter', system-ui, sans-serif;
    color: var(--sc26-ink);
    background: var(--sc26-paper);
    min-height: 100vh;
}
.sc26-topbar {
    border-bottom: 1px solid var(--sc26-line);
    background: #fff;
}
.sc26-topbar-inner {
    max-width: 1080px;
    margin: 0 auto;
    padding: 18px 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
}
.sc26-brand { display: flex; align-items: center; gap: 14px; }
.sc26-brand-mark {
    width: 44px; height: 44px;
    display: grid; place-items: center;
    background: var(--sc26-accent);
    color: #fff;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    border-radius: 10px;
    letter-spacing: 0.04em;
}
.sc26-brand-text { display: flex; flex-direction: column; line-height: 1.1; }
.sc26-brand-name {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 18px;
}
.sc26-brand-tag { font-size: 12px; color: var(--sc26-ink-soft); }
.sc26-byline {
    font-size: 12px;
    color: var(--sc26-ink-soft);
    max-width: 380px;
    text-align: right;
}
.sc26-main {
    max-width: 1080px;
    margin: 0 auto;
    padding: 48px 24px 64px;
}
.sc26-eyebrow {
    display: inline-block;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    font-size: 11px;
    color: var(--sc26-accent);
    font-weight: 600;
    margin-bottom: 12px;
}
.sc26-hero-h {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(28px, 4vw, 44px);
    font-weight: 700;
    line-height: 1.1;
    margin: 0 0 16px;
    letter-spacing: -0.01em;
}
.sc26-hero-sub {
    font-size: 17px;
    color: var(--sc26-ink-soft);
    max-width: 680px;
    line-height: 1.55;
    margin: 0 0 40px;
}
.sc26-h2 {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 20px;
    font-weight: 700;
    margin: 0 0 14px;
}
.sc26-form { display: flex; flex-direction: column; gap: 32px; }
.sc26-form-step {
    background: #fff;
    border: 1px solid var(--sc26-line);
    border-radius: 14px;
    padding: 24px;
}
.sc26-step-kicker { margin: -6px 0 14px; color: var(--sc26-ink-soft); font-size: 14px; }
.sc26-grid-2 {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
}
.sc26-field { display: flex; flex-direction: column; gap: 6px; }
.sc26-field-label { font-size: 13px; font-weight: 500; color: var(--sc26-ink-soft); }
.sc26-input {
    border: 1px solid var(--sc26-line);
    background: #fff;
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 16px;
    font-family: inherit;
    color: var(--sc26-ink);
    transition: border-color 0.15s;
}
.sc26-input:focus {
    outline: none;
    border-color: var(--sc26-accent);
    box-shadow: 0 0 0 3px rgba(162, 28, 175, 0.12);
}
.sc26-choice-row, .sc26-choice-col {
    display: flex; gap: 12px;
}
.sc26-choice-row { flex-wrap: wrap; }
.sc26-choice-col { flex-direction: column; }
.sc26-choice {
    flex: 1;
    min-width: 200px;
    border: 1px solid var(--sc26-line);
    background: #fff;
    border-radius: 10px;
    padding: 14px 16px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 4px;
    transition: all 0.15s;
}
.sc26-choice input { position: absolute; opacity: 0; pointer-events: none; }
.sc26-choice.is-on {
    border-color: var(--sc26-accent);
    background: rgba(162, 28, 175, 0.06);
    box-shadow: 0 0 0 2px rgba(162, 28, 175, 0.15);
}
.sc26-choice-l { font-weight: 600; font-size: 15px; }
.sc26-choice-hint { font-size: 13px; color: var(--sc26-ink-soft); }
.sc26-consent {
    margin-top: 18px;
    display: flex;
    gap: 10px;
    font-size: 13px;
    line-height: 1.45;
    color: var(--sc26-ink-soft);
}
.sc26-consent input { margin-top: 4px; flex-shrink: 0; }
.sc26-error {
    color: var(--sc26-hot);
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 10px;
    padding: 12px 14px;
    margin: 0;
}
.sc26-cta {
    font-family: 'Space Grotesk', sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    font-size: 16px;
    padding: 20px 28px;
    background: var(--sc26-accent);
    color: #fff;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    transition: transform 0.12s, opacity 0.15s, box-shadow 0.15s;
    box-shadow: 0 8px 24px -8px rgba(162, 28, 175, 0.5);
}
.sc26-cta:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 12px 28px -8px rgba(162, 28, 175, 0.65);
}
.sc26-cta:disabled { opacity: 0.45; cursor: not-allowed; }

/* RESULT VIEW */
.sc26-result-shell {
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;
}
.sc26-result-kicker {
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-size: 11px;
    font-weight: 600;
    color: var(--sc26-accent);
    margin-bottom: 10px;
}
.sc26-result-h {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(26px, 3.5vw, 38px);
    font-weight: 700;
    margin: 0 0 6px;
    letter-spacing: -0.01em;
}
.sc26-result-sub { font-size: 15px; color: var(--sc26-ink-soft); margin: 0 0 24px; }
.sc26-impact-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 14px;
}
.sc26-impact-card {
    background: #fff;
    border: 1px solid var(--sc26-line);
    border-left: 4px solid var(--sc26-accent);
    border-radius: 12px;
    padding: 18px 20px;
    display: flex;
    flex-direction: column;
    gap: 4px;
}
.sc26-impact-card.sc26-tone-hot { border-left-color: var(--sc26-hot); }
.sc26-impact-card.sc26-tone-warn { border-left-color: var(--sc26-warn); }
.sc26-impact-card.sc26-tone-cool { border-left-color: var(--sc26-cool); }
.sc26-impact-label { font-size: 12px; text-transform: uppercase; letter-spacing: 0.1em; color: var(--sc26-ink-soft); font-weight: 600; }
.sc26-impact-value {
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 26px;
    line-height: 1.15;
    color: var(--sc26-ink);
}
.sc26-impact-value-small { font-size: 15px; line-height: 1.3; }
.sc26-impact-note { font-size: 12px; color: var(--sc26-ink-soft); }

.sc26-empf-list, .sc26-rg-list {
    margin: 0 0 28px;
    padding-left: 0;
    list-style: none;
}
.sc26-empf-list li {
    background: #fff;
    border: 1px solid var(--sc26-line);
    border-radius: 10px;
    padding: 14px 18px;
    margin-bottom: 10px;
    font-size: 15px;
    line-height: 1.5;
    counter-increment: empf;
    position: relative;
    padding-left: 48px;
}
.sc26-empf-list { counter-reset: empf; }
.sc26-empf-list li::before {
    content: counter(empf);
    position: absolute;
    left: 14px; top: 14px;
    width: 24px; height: 24px;
    border-radius: 50%;
    background: var(--sc26-accent);
    color: #fff;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 700;
    font-size: 13px;
    display: grid;
    place-items: center;
}
.sc26-rg-list li {
    border-top: 1px solid var(--sc26-line);
    padding: 14px 0;
    font-size: 14px;
    display: grid;
    grid-template-columns: 200px 1fr;
    gap: 14px;
    align-items: start;
}
.sc26-rg-list li:first-child { border-top: none; }
.sc26-rg-list strong {
    font-family: 'Space Grotesk', sans-serif;
    color: var(--sc26-accent);
}
.sc26-rg-meta { display: block; font-size: 12px; color: var(--sc26-ink-soft); margin-top: 2px; }
.sc26-rg-text { color: var(--sc26-ink-soft); line-height: 1.5; }
.sc26-disclaimer {
    font-size: 12px;
    color: var(--sc26-ink-soft);
    border-top: 1px solid var(--sc26-line);
    padding-top: 14px;
    margin-top: 10px;
    line-height: 1.5;
}
.sc26-footer {
    margin-top: 56px;
    padding-top: 20px;
    border-top: 1px solid var(--sc26-line);
    display: flex;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    font-size: 11px;
    color: var(--sc26-ink-soft);
}
@media (max-width: 640px) {
    .sc26-rg-list li { grid-template-columns: 1fr; }
    .sc26-byline { text-align: left; max-width: none; }
}

/* ── Trust strip (images + cites + byline) — sc26-* unique treatment ── */
.sc26-trustbar {
    background: #f7f3ea;
    padding: 32px 18px;
    border-top: 1px solid rgba(20,17,13,0.08);
    border-bottom: 1px solid rgba(20,17,13,0.08);
}
.sc26-trustbar__inner {
    max-width: 1120px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 22px;
    align-items: start;
}
@media (min-width: 820px) {
    .sc26-trustbar__inner {
        grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
        gap: 36px;
    }
}
.sc26-trustbar__photo {
    margin: 0;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 6px;
    overflow: hidden;
    position: relative;
    background: #fff;
    border: 10px solid #fff; outline: 1px solid rgba(20,17,13,0.08); box-shadow: 0 12px 28px -14px rgba(20,17,13,0.30);
}
.sc26-trustbar__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.sc26-trustbar__chip {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255,255,255,0.94);
    color: #14110d;
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(20,17,13,0.08);
    backdrop-filter: blur(6px);
    margin: 0;
}
.sc26-trustbar__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.sc26-trustbar__lead {
    margin: 0;
    color: #3f3a32;
    font-size: 15px;
    line-height: 1.5;
    font-family: 'Inter', system-ui, sans-serif;
}
.sc26-trustbar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.sc26-trustbar__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #A21CAF55;
    color: #14110d;
    background: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
}
.sc26-trustbar__byline {
    display: flex;
    align-items: center;
    gap: 12px;
}
.sc26-trustbar__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #A21CAF;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}
.sc26-trustbar__bytxt {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}
.sc26-trustbar__byname {
    font-size: 13px;
    font-weight: 600;
    color: #14110d;
}
.sc26-trustbar__byrole {
    font-size: 12px;
    color: #3f3a32;
}
.sc26-trustbar__feature {
    margin: 8px 0 0;
    max-width: 360px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(20,17,13,0.10);
    box-shadow: 0 6px 22px -16px rgba(20,17,13,0.20);
    background: #fff;
}
.sc26-trustbar__feature img {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    display: block;
}
.sc26-trustbar__feature figcaption {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #3f3a32;
    padding: 8px 12px;
    background: #fff;
}


.sc26-legal{margin:32px auto 16px;text-align:center;font-size:13px;color:#9ca3af}.sc26-legal a{color:inherit;text-decoration:none;margin:0 8px}.sc26-legal a:hover{color:#A21CAF;text-decoration:underline}

</style>
